import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { useBranch } from "@/context/BranchContext";
import { fmtRM, parseFormula, formulaLabel } from "@/lib/format";
import { toast } from "sonner";
import { Calculator, CheckCircle2, XCircle, Loader2, Building2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const RISK_TAGS = ["None", "Bankrupt", "Lost Contact"];

const inputCls =
  "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold tracking-wide uppercase text-slate-500">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

export default function NewLoan() {
  const navigate = useNavigate();
  const { t: tr, tv } = useLang();
  const { branch, setBranch, branches, canViewAll } = useBranch();
  // When "All Branches" is selected, the user must pick a target branch for the new loan
  const [targetBranch, setTargetBranch] = useState(branch !== "all" ? branch : "");
  const activeBranchCode = branch !== "all" ? branch : targetBranch;
  const prefix = branches.find((b) => b.code === activeBranchCode)?.prefix || activeBranchCode || "";
  const [form, setForm] = useState({
    code: "",
    dateApplied: new Date().toISOString().slice(0, 10),
    loanType: "personal",
    cycleDays: "",
    dueDay: "",
    customerName: "",
    loanApply: "",
    stamping: "",
    commission: "",
    repaymentFormula: "",
    notes: "",
    riskTag: "None",
    status: "Active",
    phone: "",
    address: "",
    icPassport: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [dueHint, setDueHint] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [seqSuffix, setSeqSuffix] = useState("");

  const fetchSuggestions = async (q) => {
    if (!activeBranchCode || q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const { data } = await api.get("/loans/suggest", { params: { q: q.trim(), branch: activeBranchCode } });
      setSuggestions(data.suggestions || []);
      setShowSuggest(true);
    } catch {
      setSuggestions([]);
    }
  };

  const onCodeChange = (v) => {
    set("code", v);
    setSeqSuffix("");
    fetchSuggestions(v);
  };

  const selectSuggestion = async (s) => {
    const base = (s.code || "").replace(/-\d+$/, ""); // strip trailing -NN → KV060
    const baseSuffix =
      prefix && base.toUpperCase().startsWith(prefix.toUpperCase())
        ? base.slice(prefix.length)
        : base; // 060
    setForm((f) => ({
      ...f,
      code: baseSuffix || f.code,
      customerName: s.customerName || f.customerName,
      dueDay: s.dueDay ? String(s.dueDay) : f.dueDay,
      phone: s.phone || f.phone,
      icPassport: s.icPassport || f.icPassport,
      address: s.address || f.address,
    }));
    setSuggestions([]);
    setShowSuggest(false);
    try {
      const { data } = await api.get("/loans/next-sequence", { params: { base, branch: activeBranchCode } });
      setSeqSuffix(data.nextSequence || "");
    } catch {
      setSeqSuffix("");
    }
  };

  const fetchDueHint = async () => {
    const name = form.customerName.trim();
    if (!name || !activeBranchCode) { setDueHint(null); return; }
    try {
      const { data } = await api.get(`/customers/due-day-hint`, { params: { name, branch: activeBranchCode } });
      if (data && data.dueDay) {
        setDueHint(data);
        if (!form.dueDay) set("dueDay", String(data.dueDay));
      } else {
        setDueHint(null);
      }
    } catch {
      setDueHint(null);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const calc = useMemo(() => {
    const la = parseFloat(form.loanApply) || 0;
    const st = parseFloat(form.stamping) || 0;
    const co = parseFloat(form.commission) || 0;
    const inHand = la - st;              // customer receives Loan − Stamping
    const moneyUsed = inHand + co;       // our real cash out = In Hand + Commission (agent cost)
    return { inHand, moneyUsed };
  }, [form.loanApply, form.stamping, form.commission]);

  const parsed = useMemo(() => parseFormula(form.repaymentFormula), [form.repaymentFormula]);

  const submit = async (e) => {
    e.preventDefault();
    if (!activeBranchCode) {
      toast.error(tr("nl.err_select_branch"));
      return;
    }
    if (!form.code || !form.customerName || !form.loanApply) {
      toast.error(tr("nl.err_required"));
      return;
    }
    // Auto-prefix the loan code with the branch prefix
    let fullCode = form.code.toUpperCase().startsWith(prefix.toUpperCase())
      ? form.code
      : `${prefix}${form.code}`;
    // Append the auto-computed next loan-sequence number (e.g. KV060-07)
    if (seqSuffix) fullCode = `${fullCode}-${seqSuffix}`;
    setSubmitting(true);
    try {
      await api.post(
        "/loans",
        {
          ...form,
          code: fullCode,
          loanApply: parseFloat(form.loanApply) || 0,
          stamping: parseFloat(form.stamping) || 0,
          commission: parseFloat(form.commission) || 0,
          loanType: form.loanType,
          cycleDays: form.loanType === "business" ? (parseInt(form.cycleDays, 10) || null) : null,
          dueDay: form.dueDay ? parseInt(form.dueDay, 10) : null,
        },
        { params: { branch: activeBranchCode } }
      );
      toast.success(`Loan ${fullCode} created in ${activeBranchCode}. Ledger entry recorded.`);
      navigate(`/loans/${fullCode}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || tr("nl.err_create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <PageHeader title={tr("nl.title")} subtitle={tr("nl.subtitle")} />

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-5">
          {canViewAll && branch === "all" && (
            <div data-testid="newloan-branch-picker" className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <label className="text-xs font-semibold tracking-wide uppercase text-amber-800">{tr("nl.create_in_branch")}</label>
              <select className={`${inputCls} mt-1.5`} value={targetBranch} onChange={(e) => setTargetBranch(e.target.value)} data-testid="newloan-branch-select">
                <option value="">{tr("nl.select_branch")}</option>
                {branches.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={tr("nl.loan_code")}>
              <div className="flex items-center relative">
                {prefix && <span data-testid="loan-code-prefix" className="inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-slate-300 bg-slate-100 text-sm font-mono-fig font-semibold text-slate-600">{prefix}</span>}
                <input
                  data-testid="loan-code-input"
                  className={`${inputCls} ${prefix ? "rounded-l-none" : ""} ${seqSuffix ? "rounded-r-none" : ""}`}
                  value={form.code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  onFocus={() => { if (suggestions.length) setShowSuggest(true); }}
                  onBlur={() => setTimeout(() => setShowSuggest(false), 180)}
                  autoComplete="off"
                  placeholder="e.g. 072"
                />
                {seqSuffix && (
                  <span data-testid="loan-code-seq" className="inline-flex items-center px-3 py-2 rounded-r-md border border-l-0 border-slate-300 bg-slate-100 text-sm font-mono-fig font-semibold text-slate-600">-{seqSuffix}</span>
                )}
                {showSuggest && suggestions.length > 0 && (
                  <div data-testid="code-suggest-dropdown" className="absolute left-0 right-0 top-full z-20 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
                    {suggestions.map((s) => (
                      <button
                        type="button"
                        key={s.code}
                        data-testid={`code-suggest-${s.code}`}
                        onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-3 border-b border-slate-50 last:border-b-0"
                      >
                        <span className="font-mono-fig font-semibold text-slate-900">{s.code}</span>
                        <span className="text-slate-600 truncate">{s.customerName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {seqSuffix && (
                <p data-testid="loan-code-fullpreview" className="mt-1.5 text-xs text-slate-500">
                  New loan code: <span className="font-mono-fig font-semibold text-slate-800">{prefix}{form.code}-{seqSuffix}</span>
                </p>
              )}
            </Field>
            <Field label={tr("nl.date_applied")}>
              <input data-testid="loan-date-input" type="date" className={inputCls} value={form.dateApplied} onChange={(e) => set("dateApplied", e.target.value)} />
            </Field>
          </div>

          <Field label={tr("nl.loan_type")}>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="loan-type-personal"
                onClick={() => set("loanType", "personal")}
                className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${form.loanType === "personal" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white hover:border-slate-400"}`}
              >
                <div className="text-sm font-semibold">{tr("nl.personal")}</div>
                <div className={`text-xs ${form.loanType === "personal" ? "text-slate-300" : "text-slate-500"}`}>{tr("nl.personal_desc")}</div>
              </button>
              <button
                type="button"
                data-testid="loan-type-business"
                onClick={() => set("loanType", "business")}
                className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${form.loanType === "business" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white hover:border-slate-400"}`}
              >
                <div className="text-sm font-semibold">{tr("nl.business")}</div>
                <div className={`text-xs ${form.loanType === "business" ? "text-slate-300" : "text-slate-500"}`}>{tr("nl.business_desc")}</div>
              </button>
            </div>
          </Field>

          {form.loanType === "business" ? (
            <Field label={tr("nl.cycle_days")}>
              <input
                data-testid="loan-cycledays-input"
                type="number"
                min="1"
                className={inputCls}
                value={form.cycleDays}
                onChange={(e) => set("cycleDays", e.target.value)}
                placeholder="e.g. 7, 10, 14, 15 — days between payments"
              />
              {form.cycleDays && form.dateApplied && (() => {
                const c = parseInt(form.cycleDays, 10);
                if (!c) return null;
                const d = new Date(form.dateApplied);
                d.setDate(d.getDate() + c - 1);
                return (
                  <p data-testid="cycle-firstdue" className="mt-1.5 text-xs text-emerald-700">
                    First payment due: <strong>{d.toISOString().slice(0, 10)}</strong> ({c} days from apply, then {c} days from each payment).
                  </p>
                );
              })()}
            </Field>
          ) : (
            <Field label={tr("nl.salary_day")}>
              <input data-testid="loan-dueday-input" type="number" min="1" max="31" className={inputCls} value={form.dueDay} onChange={(e) => set("dueDay", e.target.value)} placeholder="e.g. 28 — customer's salary day" />
              {dueHint && dueHint.dueDay && (
                <p data-testid="dueday-hint" className="mt-1.5 text-xs text-emerald-700 flex items-center gap-1.5">
                  <Calculator className="w-3.5 h-3.5" />
                  <span>{dueHint.customerName} usually pays on the <strong>{dueHint.dueDay}{ordinal(dueHint.dueDay)}</strong> (from {dueHint.paymentCount} payment{dueHint.paymentCount === 1 ? "" : "s"} across {dueHint.loanCount} loan{dueHint.loanCount === 1 ? "" : "s"}).</span>
                  <button type="button" data-testid="dueday-hint-apply" onClick={() => set("dueDay", String(dueHint.dueDay))} className="underline font-medium hover:text-emerald-900">Use {dueHint.dueDay}</button>
                </p>
              )}
            </Field>
          )}

          <Field label={tr("nl.customer_name")}>
            <input data-testid="loan-customer-input" className={inputCls} value={form.customerName} onChange={(e) => set("customerName", e.target.value)} onBlur={fetchDueHint} placeholder={tr("nl.full_name")} />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label={tr("nl.phone")}>
              <input data-testid="loan-phone-input" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="e.g. 012-3456789" />
            </Field>
            <Field label={tr("nl.ic")}>
              <input data-testid="loan-ic-input" className={inputCls} value={form.icPassport} onChange={(e) => set("icPassport", e.target.value)} placeholder="e.g. 900101-14-5678" />
            </Field>
            <Field label={tr("nl.address")}>
              <input data-testid="loan-address-input" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder={tr("nl.address_ph")} />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label={tr("nl.loan_apply")}>
              <input data-testid="loan-apply-input" type="number" step="0.01" className={inputCls} value={form.loanApply} onChange={(e) => set("loanApply", e.target.value)} placeholder="0.00" />
            </Field>
            <Field label={tr("nl.stamping")}>
              <input data-testid="loan-stamping-input" type="number" step="0.01" className={inputCls} value={form.stamping} onChange={(e) => set("stamping", e.target.value)} placeholder="0.00" />
            </Field>
            <Field label={tr("nl.commission")}>
              <input data-testid="loan-commission-input" type="number" step="0.01" className={inputCls} value={form.commission} onChange={(e) => set("commission", e.target.value)} placeholder="0.00" />
            </Field>
          </div>

          <Field label={tr("nl.formula")}>
            <input data-testid="loan-formula-input" className={`${inputCls} font-mono-fig`} value={form.repaymentFormula} onChange={(e) => set("repaymentFormula", e.target.value)} placeholder="160+170(12M)  |  i=400  |  9000 x 1M" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={tr("nl.risk_tag")}>
              <select data-testid="loan-risk-select" className={inputCls} value={form.riskTag} onChange={(e) => set("riskTag", e.target.value)}>
                {RISK_TAGS.map((r) => <option key={r} value={r}>{tv(r)}</option>)}
              </select>
            </Field>
            <Field label={tr("nl.initial_status")}>
              <select data-testid="loan-status-select" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="Active">{tv("Active")}</option>
                <option value="Bad Debt">{tv("Bad Debt")}</option>
              </select>
            </Field>
          </div>

          <Field label={tr("nl.notes")}>
            <textarea data-testid="loan-notes-input" rows={3} className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={tr("nl.notes_ph")} />
          </Field>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => navigate("/loans")} className="bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-md px-4 py-2 text-sm font-medium">{tr("nl.cancel")}</button>
            <button data-testid="loan-submit-button" type="submit" disabled={submitting} className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} {tr("nl.create_loan")}
            </button>
          </div>
        </div>

        {/* Live preview */}
        <div className="space-y-4">
          <div data-testid="loan-calc-preview" className="bg-slate-50 border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 text-slate-700 mb-4">
              <Calculator className="h-4 w-4" />
              <span className="font-heading font-semibold text-sm">{tr("nl.live_calc")}</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-wide text-slate-500">{tr("nl.in_hand")}</span>
                <span data-testid="preview-inhand" className="font-mono-fig text-lg font-bold text-slate-900">{fmtRM(calc.inHand)}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-wide text-slate-500">{tr("nl.money_used")}</span>
                <span data-testid="preview-moneyused" className="font-mono-fig text-lg font-bold text-slate-900">{fmtRM(calc.moneyUsed)}</span>
              </div>
              <div className="text-[11px] text-slate-400 pt-1 border-t border-slate-200">
                {tr("nl.calc_formula")}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="font-heading font-semibold text-sm text-slate-700 mb-3">{tr("nl.formula_parser")}</div>
            {form.repaymentFormula ? (
              <div data-testid="formula-preview" className={`rounded-md border p-3 ${parsed.valid ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {parsed.valid ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
                  <span className={`text-sm font-semibold ${parsed.valid ? "text-green-700" : "text-red-700"}`}>{formulaLabel(parsed.type)}</span>
                </div>
                {parsed.valid && (
                  <div className="space-y-1 text-xs text-slate-600 font-mono-fig">
                    {parsed.monthlyInterest > 0 && <div>{tr("nl.monthly_interest")}: <b>{fmtRM(parsed.monthlyInterest)}</b></div>}
                    {parsed.monthlyPrincipal > 0 && <div>{tr("nl.monthly_principal")}: <b>{fmtRM(parsed.monthlyPrincipal)}</b></div>}
                    {parsed.settlementAmount > 0 && <div>{tr("nl.settlement")}: <b>{fmtRM(parsed.settlementAmount)}</b></div>}
                    {parsed.durationMonths && <div>{tr("nl.duration")}: <b>{parsed.durationMonths} {tr("nl.months")}</b></div>}
                  </div>
                )}
                {!parsed.valid && <div className="text-xs text-red-600">{tr("nl.unrecognized")}</div>}
              </div>
            ) : (
              <div className="text-xs text-slate-400">{tr("nl.enter_formula")}</div>
            )}
          </div>
        </div>
      </form>
    </Layout>
  );
}
